"use client";

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import LanguageSelector from "../Common/LanguageSelector";

const isElectron = () => typeof window !== "undefined" && !!window.api;
const asset = (name) => `${process.env.PUBLIC_URL || ""}/${name}`;

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.3 35.3 26.8 36 24 36c-5.3 0-9.7-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.2-3.5 5.7-6.6 7.2l6.3 5.3C37.4 38.3 44 33 44 24c0-1.2-.1-2.3-.4-3.5z" />
  </svg>
);

export function LoginPage({
  onLogin,
  onSocialAuth,
  onModeSwitch,
  onForgotPassword,
  initialEmail = "",
  error: externalError = "",
  infoMessage = "",
  isLoading: externalIsLoading = false,
  mode = "login",
  rememberMe,
  setRememberMe,
  registerCodeSent = false,
  resetCodeSent = false,
  resetCodeVerified = false,
  onCancelVerify,
}) {
  const { t } = useTranslation("auth");
  const electron = isElectron();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState(initialEmail);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const otpRefs = useRef([]);
  const [otp, setOtp] = useState(["", "", "", "", ""]);

  const isVerifyStep = (mode === "register" && registerCodeSent) || (mode === "forgot" && resetCodeSent && !resetCodeVerified);
  const isPasswordStep = mode === "forgot" && resetCodeVerified;
  const showTabs = !isVerifyStep && !isPasswordStep && mode !== "forgot";

  useEffect(() => {
    setEmail(initialEmail);
  }, [initialEmail]);

  useEffect(() => {
    setOtp(["", "", "", "", ""]);
    setVerificationCode("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    if (mode !== "register") setName("");
  }, [mode, registerCodeSent, resetCodeSent, resetCodeVerified]);

  const handleOtpChange = (index, value) => {
    if (!/^[0-9A-Za-z]?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    setVerificationCode(next.join(""));
    if (value && index < 4 && otpRefs.current[index + 1]) otpRefs.current[index + 1].focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0 && otpRefs.current[index - 1]) {
      otpRefs.current[index - 1].focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/[^0-9A-Za-z]/g, "").substring(0, 5);
    const next = ["", "", "", "", ""];
    for (let i = 0; i < pasted.length; i += 1) next[i] = pasted[i];
    setOtp(next);
    setVerificationCode(next.join(""));
    const focusAt = Math.min(pasted.length, 4);
    if (otpRefs.current[focusAt]) otpRefs.current[focusAt].focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin({ email, password, confirmPassword, verificationCode, name });
  };

  const openExternal = (url) => {
    if (window.api?.openExternal) {
      window.api.openExternal(url);
      return;
    }
    window.location.href = url;
  };

  const submitLabel = externalIsLoading
    ? t("buttons.processing")
    : isVerifyStep
      ? t("buttons.verifyCode")
      : isPasswordStep
        ? t("buttons.updatePassword")
        : mode === "login"
          ? t("buttons.loginSubmit")
          : mode === "register"
            ? t("buttons.registerSubmit")
            : t("buttons.sendResetCode");

  const fieldClass =
    "w-full h-11 rounded-xl bg-[#161f31] border border-white/16 px-3.5 text-[0.92rem] text-[#e8f0fa] placeholder:text-[#7a96b0] outline-none transition-[border-color,box-shadow] duration-200 focus:border-[#3d8ef0]/70 focus:shadow-[0_0_0_3px_rgba(61,142,240,0.16)]";

  const passwordToggle = (visible, onToggle, hideLabel, showLabel) => (
    <button
      type="button"
      onClick={onToggle}
      className="absolute end-3 top-1/2 -translate-y-1/2 text-[#8aaccc] hover:text-white"
      aria-label={visible ? hideLabel : showLabel}
    >
      {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
    </button>
  );

  const topActions = typeof document !== "undefined"
    ? createPortal(
      <div className="auth-top-actions no-drag" dir="ltr">
        <LanguageSelector variant="compact" className="auth-lang-selector" />
        {!electron && (
          <a
            href="https://notesplus.bosph.net/"
            className="auth-back-home"
          >
            <span>{t("backHome")}</span>
            <span aria-hidden="true">←</span>
          </a>
        )}
      </div>,
      document.body
    )
    : null;

  return (
    <>
    {topActions}
    <div className="auth-shell relative min-h-screen w-full overflow-x-hidden overflow-y-auto">
      <div className="auth-shell-bg" aria-hidden="true" />

      <div className="relative z-10 flex min-h-screen flex-col px-4 pb-8 pt-16 sm:px-6">
        <div className="flex flex-1 items-center justify-center">
          <div className="auth-card w-full max-w-[420px] overflow-visible rounded-2xl border border-[#3d8ef0]/24 bg-[#0d1424]/64 p-6 shadow-[0_18px_50px_rgba(5,10,22,0.48)] backdrop-blur-[12px] sm:p-7">
            <div className="mb-5 text-center">
              <img
                src={asset("darkmode.png")}
                alt="Notes+"
                className="mx-auto mb-2.5 h-10 w-auto object-contain"
              />
              <p className="text-[0.92rem] text-[#9bb4ce]">{t("tagline")}</p>
            </div>

            {showTabs && (
              <div className="mb-5 grid grid-cols-2 gap-1 rounded-full border border-white/[0.06] bg-[#080e1a]/70 p-1">
                <button
                  type="button"
                  onClick={() => onModeSwitch("login")}
                  className={`h-9 rounded-full text-sm font-semibold transition-all duration-200 ${
                    mode === "login"
                      ? "bg-[#3d8ef0] text-white shadow-[0_0_16px_rgba(61,142,240,0.28)]"
                      : "text-[#9bb4ce] hover:text-white"
                  }`}
                >
                  {t("tabs.login")}
                </button>
                <button
                  type="button"
                  onClick={() => onModeSwitch("register")}
                  className={`h-9 rounded-full text-sm font-semibold transition-all duration-200 ${
                    mode === "register"
                      ? "bg-[#3d8ef0] text-white shadow-[0_0_16px_rgba(61,142,240,0.28)]"
                      : "text-[#9bb4ce] hover:text-white"
                  }`}
                >
                  {t("tabs.register")}
                </button>
              </div>
            )}

            {(isVerifyStep || isPasswordStep || mode === "forgot") && (
              <div className="mb-4 text-center">
                <h1 className="text-xl font-semibold tracking-tight text-white">
                  {isVerifyStep ? t("checkEmail.title") : isPasswordStep ? t("newPassword.title") : t("heading.forgot")}
                </h1>
                <p className="mt-1.5 text-sm text-[#9bb4ce]">
                  {isVerifyStep ? (
                    <>
                      {t("checkEmail.descriptionPrefix")} <strong className="text-[#e8f0fa]">{email}</strong>
                      {t("checkEmail.descriptionSuffix")}
                    </>
                  ) : isPasswordStep ? (
                    t("newPassword.description")
                  ) : (
                    t("subheading.forgot")
                  )}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3.5">
              {!isVerifyStep && !isPasswordStep && (
                <div key={mode} className="space-y-3.5 animate-[fadeSlide_220ms_ease]">
                  {mode === "register" && (
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-medium text-[#c5d6ea]">{t("fields.name")}</span>
                      <input
                        type="text"
                        autoComplete="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t("fields.namePlaceholder")}
                        required
                        className={fieldClass}
                      />
                    </label>
                  )}

                  <label className="block">
                    <span className="mb-1.5 block text-xs font-medium text-[#c5d6ea]">{t("fields.email")}</span>
                    <input
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("fields.emailPlaceholder")}
                      required
                      className={fieldClass}
                    />
                  </label>

                  {mode !== "forgot" && (
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-medium text-[#c5d6ea]">{t("fields.password")}</span>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          autoComplete={mode === "login" ? "current-password" : "new-password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          required
                          className={`${fieldClass} pe-11`}
                        />
                        {passwordToggle(showPassword, () => setShowPassword(!showPassword), t("fields.hidePassword"), t("fields.showPassword"))}
                      </div>
                    </label>
                  )}

                  {mode === "register" && (
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-medium text-[#c5d6ea]">{t("fields.confirmPassword")}</span>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          autoComplete="new-password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="••••••••"
                          required
                          className={`${fieldClass} pe-11`}
                        />
                        {passwordToggle(showConfirmPassword, () => setShowConfirmPassword(!showConfirmPassword), t("fields.hidePassword"), t("fields.showPassword"))}
                      </div>
                    </label>
                  )}

                  {mode === "login" && (
                    <div className="flex items-center justify-between text-sm">
                      <label className="inline-flex cursor-pointer items-center gap-2 text-[#c5d6ea]">
                        <input
                          type="checkbox"
                          checked={!!rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="size-3.5 rounded border-white/20 bg-transparent accent-[#3d8ef0]"
                        />
                        {t("rememberMe")}
                      </label>
                      <button type="button" onClick={onForgotPassword} className="text-[#7eb6f5] hover:text-white">
                        {t("forgotPassword")}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {isPasswordStep && (
                <div className="space-y-3.5 animate-[fadeSlide_220ms_ease]">
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-medium text-[#c5d6ea]">{t("fields.newPassword")}</span>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className={`${fieldClass} pe-11`}
                      />
                      {passwordToggle(showPassword, () => setShowPassword(!showPassword), t("fields.hidePassword"), t("fields.showPassword"))}
                    </div>
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-medium text-[#c5d6ea]">{t("fields.confirmPassword")}</span>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className={`${fieldClass} pe-11`}
                      />
                      {passwordToggle(showConfirmPassword, () => setShowConfirmPassword(!showConfirmPassword), t("fields.hidePassword"), t("fields.showPassword"))}
                    </div>
                  </label>
                </div>
              )}

              {isVerifyStep && (
                <div className="flex justify-center gap-2 py-2 animate-[fadeSlide_220ms_ease]">
                  {[0, 1, 2, 3, 4].map((index) => (
                    <input
                      key={index}
                      ref={(el) => { otpRefs.current[index] = el; }}
                      type="text"
                      inputMode="text"
                      maxLength={1}
                      value={otp[index]}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={handleOtpPaste}
                      autoFocus={index === 0}
                      className="h-12 w-11 rounded-xl border border-white/10 bg-[#080e1a]/70 text-center text-xl font-semibold text-white outline-none focus:border-[#3d8ef0]/70 sm:h-14 sm:w-12"
                    />
                  ))}
                </div>
              )}

              {infoMessage && !externalError && (
                <div className="rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-3 py-2.5 text-sm text-emerald-200">
                  {infoMessage}
                </div>
              )}
              {externalError && (
                <div className="rounded-xl border border-red-400/25 bg-red-500/10 px-3 py-2.5 text-sm text-red-200">
                  {externalError}
                </div>
              )}

              <button
                type="submit"
                disabled={externalIsLoading || (isVerifyStep && verificationCode.length !== 5)}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#3d8ef0] to-[#5ca3f5] text-sm font-semibold text-white shadow-[0_8px_22px_rgba(61,142,240,0.28)] transition-transform duration-200 hover:-translate-y-px disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {externalIsLoading && (
                  <span className="size-4 animate-spin rounded-full border-2 border-white/80 border-t-transparent" />
                )}
                {submitLabel}
              </button>

              {(isVerifyStep || isPasswordStep || mode === "forgot") && (
                <button
                  type="button"
                  onClick={() => {
                    if (onCancelVerify) onCancelVerify();
                    if (mode === "forgot") onModeSwitch("login");
                  }}
                  className="mx-auto flex items-center gap-1 text-sm text-[#9bb4ce] hover:text-white"
                >
                  ← {t("goBack")}
                </button>
              )}
            </form>

            {!isVerifyStep && !isPasswordStep && mode !== "forgot" && (
              <>
                <div className="my-4 flex items-center gap-3 text-[0.7rem] uppercase tracking-[0.16em] text-[#7a96b0]">
                  <span className="h-px flex-1 bg-white/10" />
                  {t("or")}
                  <span className="h-px flex-1 bg-white/10" />
                </div>
                <button
                  type="button"
                  onClick={() => onSocialAuth("google")}
                  disabled={externalIsLoading}
                  className="flex h-11 w-full items-center justify-center gap-2.5 rounded-xl border border-white/12 bg-[#080e1a]/45 text-sm font-medium text-[#e8f0fa] transition-colors duration-200 hover:border-[#3d8ef0]/45 hover:shadow-[0_0_0_3px_rgba(61,142,240,0.12)] disabled:opacity-60"
                >
                  <GoogleMark />
                  {t("buttons.googleContinue")}
                </button>
              </>
            )}
          </div>
        </div>

        <footer className="mt-6 flex flex-wrap items-center justify-between gap-2 pb-1 text-[0.72rem] text-[#7d97b3]">
          <span>{t("footerRights")}</span>
          <div className="flex items-center gap-3">
            <button type="button" className="hover:text-white" onClick={() => openExternal("https://notesplus.bosph.net/privacy/")}>
              {t("footer.privacy")}
            </button>
            <button type="button" className="hover:text-white" onClick={() => openExternal("https://notesplus.bosph.net/terms/")}>
              {t("footer.terms")}
            </button>
            <button type="button" className="hover:text-white" onClick={() => openExternal("https://notesplus.bosph.net/iletisim")}>
              {t("footer.contact")}
            </button>
          </div>
        </footer>
      </div>

      <style>{`
        .auth-top-actions {
          position: fixed !important;
          top: 16px !important;
          right: 16px !important;
          left: auto !important;
          z-index: 2147483000 !important;
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          justify-content: flex-end !important;
          gap: 8px !important;
          pointer-events: auto !important;
          -webkit-app-region: no-drag !important;
        }
        .auth-lang-selector,
        .auth-lang-selector > button {
          height: 36px;
        }
        .auth-lang-selector > button {
          min-width: 76px;
          padding: 0 12px;
          border: 1px solid rgba(255,255,255,0.22);
          background: rgba(13, 20, 36, 0.92);
          box-shadow: 0 8px 24px rgba(5,10,22,0.45);
        }
        .auth-back-home {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          height: 36px;
          padding: 0 14px;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.22);
          background: rgba(13, 20, 36, 0.92);
          color: #c5d6ea;
          font-size: 12px;
          font-weight: 500;
          text-decoration: none;
          backdrop-filter: blur(12px);
          -webkit-app-region: no-drag;
        }
        .auth-back-home:hover { color: #fff; border-color: rgba(61,142,240,0.45); }
        @media (max-width: 480px) {
          .auth-top-actions {
            top: 10px !important;
            right: 10px !important;
            left: 10px !important;
            flex-wrap: wrap;
          }
        }
        .auth-shell { background-color: #050a16; }
        .auth-shell-bg {
          position: fixed;
          inset: 0;
          z-index: 0;
          background-color: #050a16;
          background-image: url(${asset("notespluswebbc.png")});
          background-repeat: no-repeat;
          background-size: cover;
          background-position: center 40%;
          pointer-events: none;
        }
        @keyframes fadeSlide {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 1440px) and (max-height: 900px) {
          .auth-shell-bg { background-position: center 36%; }
        }
        @media (max-width: 1366px) {
          .auth-shell-bg { background-position: center 34%; }
        }
        @media (max-width: 680px) {
          .auth-shell-bg { background-position: 72% 28%; }
        }
      `}</style>
    </div>
    </>
  );
}
